import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TokenStyles.css';
import FeatureTiers from '../FeatureTiers';

const TokenDisplay = ({ isAuthenticated, username, userTier = 'student', hasFeatureAccess, user, minimal = false, isGuest = false, guestTokens = 0, onTokenStoreClick, cognitiveState = null, bloomLevel = null }) => {
  const [tokenBalance, setTokenBalance] = useState(null);
  const [streakDays, setStreakDays] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showReferralInfo, setShowReferralInfo] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [showApplyReferral, setShowApplyReferral] = useState(false);
  const [applyReferralCode, setApplyReferralCode] = useState('');
  const [applyReferralMessage, setApplyReferralMessage] = useState('');
  const [pendingGifts, setPendingGifts] = useState([]);
  const [showGiftCelebration, setShowGiftCelebration] = useState(false);
  const [networkStats, setNetworkStats] = useState(null);

  // Phase 3.5 Gamification: Flow state and multiplier tracking
  const [currentMultiplier, setCurrentMultiplier] = useState(1.0);
  const [recentTokens, setRecentTokens] = useState(0);
  const [showTokenAnimation, setShowTokenAnimation] = useState(false);

  // Daily quests progress for header hint
  const [questProgress, setQuestProgress] = useState({ completed: 0, total: 0, hasClaimable: false });

  // Determine tier information early for use throughout component
  const hasTokenEconomy = FeatureTiers.hasAccess('TOKEN_ECONOMY', user);
  const hasTokenMultiplier = FeatureTiers.hasAccess('TOKEN_MULTIPLIER', user);
  const hasNetworkEffects = FeatureTiers.hasAccess('FRIEND_NETWORK_EFFECTS', user);
  const detectedTier = user ? FeatureTiers.getUserTier(user) : 'guest';
  const isUnlimitedUser = userTier === 'unlimited' || detectedTier === 'unlimited' || hasTokenMultiplier;
  const isHomeUser = userTier === 'home' || detectedTier === 'home';
  // Home users get all Unlimited features plus family bonuses (tier 3.5 > 3)
  const hasFullTokenAccess = hasTokenEconomy || hasTokenMultiplier; // This covers both Unlimited and Home users
  const isPremiumOrHigher = userTier === 'premium' || userTier === 'expert' || userTier === 'unlimited' || userTier === 'home' ||
                           detectedTier === 'premium' || detectedTier === 'expert' || detectedTier === 'unlimited' || detectedTier === 'home' ||
                           hasTokenEconomy || hasTokenMultiplier;

  useEffect(() => {
    if (isAuthenticated) {
      fetchTokenBalance();
      fetchPendingGifts();
      fetchNetworkStats();
      fetchQuestProgress();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Phase 3.5: Calculate multiplier from cognitive state
  useEffect(() => {
    if (!cognitiveState) return;

    let multiplier = 1.0;

    // Flow state: 3x multiplier
    if (cognitiveState.flow_state) {
      multiplier = 3.0;
    }
    // Learning style match: 1.5x (would come from parent component)
    // Bloom level bonus: handled separately

    setCurrentMultiplier(multiplier);
  }, [cognitiveState]);

  // Detect flow state
  const isInFlowState = cognitiveState?.flow_state || false;
  const mentalEnergy = cognitiveState?.mental_energy || 0.7;
  const focusLevel = cognitiveState?.focus_level || 0.6;

  // Bloom's taxonomy levels for visualization
  const bloomLevels = {
    'remembering': { label: 'Remembering', color: '#94A3B8', icon: '📝', level: 1 },
    'understanding': { label: 'Understanding', color: '#60A5FA', icon: '💡', level: 2 },
    'applying': { label: 'Applying', color: '#34D399', icon: '🎯', level: 3 },
    'analyzing': { label: 'Analyzing', color: '#FBBF24', icon: '🔍', level: 4 },
    'evaluating': { label: 'Evaluating', color: '#F97316', icon: '⚖️', level: 5 },
    'creating': { label: 'Creating', color: '#E879F9', icon: '✨', level: 6 }
  };

  const currentBloom = bloomLevel ? bloomLevels[bloomLevel.toLowerCase()] : null;

  const fetchTokenBalance = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/tokens/balance');
      setTokenBalance(response.data.balance);
      
      // Fetch streak days if available
      if (response.data.streak_days) {
        setStreakDays(response.data.streak_days);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching token balance:', error);
      setIsLoading(false);
    }
  };

  const fetchPendingGifts = async () => {
    try {
      const response = await axios.get('/api/tokens/pending-gifts');
      setPendingGifts(response.data.pending_gifts || []);
      
      // Show celebration if there are new gifts
      if (response.data.pending_gifts && response.data.pending_gifts.length > 0) {
        setShowGiftCelebration(true);
      }
    } catch (error) {
      console.error('Error fetching pending gifts:', error);
    }
  };

  const fetchNetworkStats = async () => {
    try {
      const response = await axios.get('/api/tokens/network-stats');
      setNetworkStats(response.data);
    } catch (error) {
      console.error('Error fetching network stats:', error);
      // Set default values if API call fails
      setNetworkStats({
        friend_count: 0,
        active_collaborations: 0,
        total_collaborations: 0,
        recent_friends: []
      });
    }
  };

  const fetchQuestProgress = async () => {
    try {
      const response = await axios.get('/api/tokens/daily-quests');
      const quests = response.data.quests || [];
      const completed = quests.filter(q => q.claimed).length;
      const hasClaimable = quests.some(q => q.progress >= q.target && !q.claimed);
      setQuestProgress({
        completed,
        total: quests.length,
        hasClaimable
      });
    } catch (error) {
      console.error('Error fetching quest progress:', error);
    }
  };

  const fetchReferralCode = async () => {
    try {
      const response = await axios.get('/api/referral/code');
      setReferralCode(response.data.code);
      setShowReferralInfo(true);
    } catch (error) {
      console.error('Error fetching referral code:', error);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/join?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setReferralCopied(true);
        setTimeout(() => setReferralCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleApplyReferral = async () => {
    if (!applyReferralCode.trim()) {
      setApplyReferralMessage('Please enter a referral code');
      return;
    }

    try {
      const response = await axios.post('/api/referral/apply', {
        code: applyReferralCode.trim()
      });

      if (response.data.success) {
        setApplyReferralMessage(response.data.message);
        setApplyReferralCode('');
        // Refresh token balance to show new tokens
        fetchTokenBalance();
        
        // Also refresh the header badge if available
        if (window.refreshTokenBalance) {
          window.refreshTokenBalance();
        }
        // Hide apply form after success
        setTimeout(() => {
          setShowApplyReferral(false);
          setApplyReferralMessage('');
        }, 3000);
      } else {
        setApplyReferralMessage(response.data.error);
      }
    } catch (error) {
      console.error('Error applying referral code:', error);
      setApplyReferralMessage('Error applying referral code. Please try again.');
    }
  };

  // Guest mode - show session-based tokens with iOS-style minimal display (FIRST PRIORITY)
  if (isGuest) {
    return (
      <div 
        className="token-display guest-mode"
        onClick={onTokenStoreClick}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '6px 12px',
          fontSize: '0.85rem',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <span style={{ fontSize: '0.9rem' }}>🪙</span>
        {guestTokens} tokens
        <span style={{ 
          fontSize: '0.7rem', 
          opacity: 0.8,
          color: '#FFD700'
        }}>💰</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="token-display guest-mode">
        <div 
          className="token-teaser clickable collaboration-focused"
          onClick={() => {
            // Check if user is Premium/Expert (paying customer) - redirect to bounties
            if (isPremiumOrHigher) {
              window.location.href = '/bounties';
            } else {
              // For guests, show contextual upgrade prompt rather than leaving the session
              if (window.confirm('🐠 Study with friends and earn 2x tokens! Sign up to unlock collaboration features?')) {
                window.open('/pricing?feature=collaboration', '_blank');
              }
            }
          }}
          role="button"
          tabIndex="0"
          style={{
            background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)", // Tappy Labs ecosystemFlow
            color: "#FAFAFA", // Tappy Labs clarityWhite
            borderRadius: "12px",
            padding: "12px 16px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 16px rgba(16, 185, 129, 0.2)",
            transition: "all 0.2s ease",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: '16px' }}>🪙💰</span>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '2px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              }}>
                {isPremiumOrHigher ? 'Start Team Bounty!' : 'Study with friends for bonus tokens!'}
              </div>
              <div style={{ 
                fontSize: '12px', 
                opacity: '0.9',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              }}>
                {isPremiumOrHigher ? 
                  'Form study squads • Earn collaboration multipliers • Complete challenges' : 
                  'Invite study buddies • Earn together • Get Discord Nitro & more'
                }
              </div>
            </div>
            <span className="arrow-icon" style={{ fontSize: '18px', opacity: '0.8' }}>→</span>
          </div>
          
          {/* Subtle shimmer animation using Tappy Labs keyframes */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
            animation: 'shimmer 2s ease-in-out infinite'
          }} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="token-display loading">Loading tokens...</div>;
  }

  const hasPremiumTokenFeatures = hasTokenEconomy || hasTokenMultiplier;
  
  
  // Minimal header version for clean integration
  // Phase 3.5: Enhanced with flow state and multipliers
  if (minimal) {
    return (
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: isInFlowState
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(249, 115, 22, 0.15))'
          : 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(16, 185, 129, 0.1))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)', // iOS Safari
        border: isInFlowState
          ? '1.5px solid rgba(245, 158, 11, 0.4)'
          : '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '16px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#FAFAFA',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
        boxShadow: isInFlowState
          ? '0 8px 32px rgba(245, 158, 11, 0.3), 0 0 0 1px rgba(245, 158, 11, 0.1) inset'
          : '0 4px 24px rgba(14, 165, 233, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        userSelect: 'none',
        animation: isInFlowState ? 'flowPulse 2s ease-in-out infinite' : 'none'
      }}
      onClick={() => {
        // Haptic-style pulse animation (iOS inspired)
        const element = document.querySelector('[data-token-minimal]');
        if (element) {
          element.style.transform = 'scale(0.96)';
          setTimeout(() => {
            element.style.transform = 'scale(1)';
          }, 150);
        }
      }}
      data-token-minimal
      title={isInFlowState ? `🔥 FLOW STATE ACTIVE! ${currentMultiplier}x multiplier` : `${tokenBalance || 0} tokens earned`}
      >
        {/* Flow state golden glow effect */}
        {isInFlowState && (
          <div style={{
            position: 'absolute',
            inset: '-2px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(249, 115, 22, 0.3))',
            borderRadius: '16px',
            filter: 'blur(12px)',
            opacity: 0.6,
            animation: 'flowGlow 2s ease-in-out infinite',
            pointerEvents: 'none',
            zIndex: -1
          }} />
        )}

        {/* Multiplier badge (2025 iOS pill style) */}
        {currentMultiplier > 1.0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: isInFlowState
              ? 'linear-gradient(135deg, #F59E0B, #F97316)'
              : 'linear-gradient(135deg, #10B981, #0EA5E9)',
            borderRadius: '12px',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: '700',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            letterSpacing: '0.5px'
          }}>
            {isInFlowState && <span>🔥</span>}
            {currentMultiplier.toFixed(1)}×
          </div>
        )}

        {/* Animated token icon */}
        <span style={{
          fontSize: '18px',
          display: 'flex',
          alignItems: 'center',
          filter: isInFlowState ? 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.6))' : 'none',
          animation: isInFlowState ? 'tokenSpin 3s ease-in-out infinite' : 'float 2s ease-in-out infinite'
        }}>
          🪙
        </span>

        {/* Token count with better typography */}
        <span style={{
          fontSize: '16px',
          fontWeight: '700',
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums', // iOS style monospaced numbers
          textShadow: isInFlowState ? '0 0 8px rgba(245, 158, 11, 0.4)' : 'none'
        }}>
          {tokenBalance || 0}
        </span>

        {/* Bloom level mini indicator (Notion-style tag) */}
        {currentBloom && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            background: `${currentBloom.color}20`,
            border: `1px solid ${currentBloom.color}40`,
            borderRadius: '8px',
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: '600',
            color: currentBloom.color
          }}>
            <span style={{ fontSize: '11px' }}>{currentBloom.icon}</span>
            {currentBloom.level}
          </div>
        )}

        {/* Modern streak indicator */}
        {streakDays > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '12px',
            background: 'linear-gradient(135deg, #F59E0B, #F97316)',
            borderRadius: '10px',
            padding: '3px 7px',
            boxShadow: '0 2px 6px rgba(245, 158, 11, 0.2)',
            fontWeight: '700'
          }}>
            🔥{streakDays}
          </div>
        )}

        {/* Daily Quest Progress Hint - opens TokenWalletModal */}
        {questProgress.total > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              background: questProgress.hasClaimable
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'rgba(100, 116, 139, 0.2)',
              borderRadius: '8px',
              padding: '3px 8px',
              fontWeight: '600',
              color: questProgress.hasClaimable ? 'white' : 'rgba(255, 255, 255, 0.8)',
              boxShadow: questProgress.hasClaimable
                ? '0 2px 8px rgba(16, 185, 129, 0.3)'
                : 'none',
              animation: questProgress.hasClaimable ? 'questPulse 2s ease-in-out infinite' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            title={questProgress.hasClaimable ? 'Quests ready to claim!' : `${questProgress.completed}/${questProgress.total} daily quests`}
          >
            <span style={{ fontSize: '10px' }}>📋</span>
            <span>{questProgress.completed}/{questProgress.total}</span>
            {questProgress.hasClaimable && (
              <span style={{ fontSize: '8px' }}>✨</span>
            )}
          </div>
        )}

        {/* Energy/Focus micro-indicators (Google Workspace style) */}
        {cognitiveState && (
          <div style={{
            display: 'flex',
            gap: '4px',
            marginLeft: '2px'
          }}>
            {/* Energy meter */}
            <div style={{
              width: '4px',
              height: '14px',
              borderRadius: '2px',
              background: mentalEnergy > 0.7 ? '#10B981' : mentalEnergy > 0.4 ? '#FBBF24' : '#EF4444',
              opacity: 0.6,
              transition: 'all 0.3s ease'
            }} title={`Energy: ${Math.round(mentalEnergy * 100)}%`} />

            {/* Focus meter */}
            <div style={{
              width: '4px',
              height: '14px',
              borderRadius: '2px',
              background: focusLevel > 0.7 ? '#10B981' : '#FBBF24',
              opacity: 0.6,
              transition: 'all 0.3s ease'
            }} title={`Focus: ${Math.round(focusLevel * 100)}%`} />
          </div>
        )}
      </div>
    );
  }


  return (
    <div className="token-display">
      <div className="token-balance">
        <span className="token-icon">🪙</span>
        <span className="token-amount">{tokenBalance || 0}</span>
        <span className="token-label">tokens</span>
        {!hasFullTokenAccess && (
          <span className="token-tier-badge limited">Token Access Limited</span>
        )}
      </div>
      
      {/* 🐠 UNLIMITED/HOME COLLABORATION DASHBOARD - Show advanced features for token economy users */}
      {hasFullTokenAccess && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(14, 165, 233, 0.05))', // Expert gradient
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '12px',
          fontSize: '12px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '16px' }}>🐠🐠🐠</span>
              <div style={{ fontWeight: '600', color: '#8B5CF6' }}>
                {isHomeUser ? 'Learning Aquarium Family' : 
                 isUnlimitedUser ? 'Learning Aquarium Unlimited' : 
                 'Learning Aquarium Advanced'}
              </div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              2x MULTIPLIER ACTIVE
            </div>
          </div>
          
          {/* Network Effects Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '6px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{ color: '#10B981', fontWeight: '600', fontSize: '11px' }}>
                Active Collaborations
              </div>
              <div style={{ color: '#059669', fontSize: '14px', fontWeight: 'bold' }}>
                {networkStats?.active_collaborations || 0} ongoing
              </div>
            </div>
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)',
              padding: '6px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(245, 158, 11, 0.2)'
            }}>
              <div style={{ color: '#F59E0B', fontWeight: '600', fontSize: '11px' }}>
                Network Size
              </div>
              <div style={{ color: '#D97706', fontSize: '14px', fontWeight: 'bold' }}>
                {networkStats?.friend_count || 0} friends
              </div>
            </div>
          </div>
          
          {/* Expert Actions - Go to Bounties or Invite Friends */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div 
              onClick={() => window.location.href = '/bounties'}
              style={{
                background: 'linear-gradient(135deg, #0EA5E9, #10B981)',
                color: 'white',
                padding: '6px 8px',
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
                fontSize: '10px'
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '1px' }}>
                🎯 Start Bounty
              </div>
              <div style={{ opacity: '0.9', fontSize: '9px' }}>
                Form squads
              </div>
            </div>
            
            <div 
              onClick={() => {
                // Trigger the existing friend sharing functionality from TutorInterface
                // This is contextual - invite friends to current tutoring session
                if (window.generateShareLink) {
                  window.generateShareLink();
                } else {
                  alert('Start a voice session with Mira, then you can invite friends to collaborate on this problem!');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                color: 'white',
                padding: '6px 8px',
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
                fontSize: '10px'
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '1px' }}>
                🐠 Invite Friends
              </div>
              <div style={{ opacity: '0.9', fontSize: '9px' }}>
                To this session
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 🐠 GUEST/LOWER TIER FOMO - Encourage upgrade to access full token features */}
      {!hasFullTokenAccess && tokenBalance > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(239, 68, 68, 0.05)', // Subtle urgency red
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          fontSize: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>😭</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#EF4444', marginBottom: '2px' }}>
                {networkStats?.recent_friends?.[0] ? 
                  `${networkStats.recent_friends[0].friend_name} has ${networkStats.recent_friends[0].tokens_earned_together + 100} tokens already!` :
                  'Your friends are earning tokens too!'}
              </div>
              <div style={{ color: '#64748B', fontSize: '11px' }}>
                If I work with Maya, we'd both get {Math.round(tokenBalance * 1.5)} tokens each!
              </div>
            </div>
            <button
              onClick={() => {
                // Check if user has full token access
                if (hasFullTokenAccess) {
                  window.location.href = '/bounties';
                } else {
                  // For lower tier users, show contextual upgrade prompt without leaving session
                  if (window.confirm('🐠 Unlock token economy and trading! Upgrade to Unlimited to access full token features and earn more tokens?')) {
                    window.open('/pricing?feature=tokens', '_blank');
                  }
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #10B981, #0EA5E9)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
              }}
            >
              Squad Up!
            </button>
          </div>
        </div>
      )}
      
      {/* 🎉 FRIENDSHIP GIFT CELEBRATION */}
      {pendingGifts.length > 0 && (
        <div className="friendship-gifts">
          {pendingGifts.map(gift => (
            <div key={gift.gift_id} className="friendship-gift-card">
              <div className="gift-celebration-header">
                <span className="celebration-emoji">🎉🤝</span>
                <span className="gift-title">Study Buddy Bonus!</span>
              </div>
              <div className="gift-message">
                {gift.message}
              </div>
              <div className="gift-amount">
                <span className="gift-tokens">+{gift.gift_amount}</span>
                <span className="gift-label">friendship tokens</span>
              </div>
              <div className="gift-from">
                From your study buddy: <strong>{gift.from_friend}</strong>
              </div>
              <button 
                className="claim-gift-btn"
                onClick={(e) => {
                  // Add gift tokens to balance
                  setTokenBalance(prev => prev + gift.gift_amount);
                  // Remove from pending gifts
                  setPendingGifts(prev => prev.filter(g => g.gift_id !== gift.gift_id));
                  setShowGiftCelebration(false);
                  
                  // Trigger token animation for gift claiming
                  if (window.triggerTokenAnimation) {
                    window.triggerTokenAnimation(gift.gift_amount, e.target);
                  }
                  
                  // Refresh header badge
                  if (window.refreshTokenBalance) {
                    window.refreshTokenBalance();
                  }
                }}
              >
                🎁 Claim Your Friendship Tokens!
              </button>
            </div>
          ))}
        </div>
      )}
      
      {!hasPremiumTokenFeatures && (
        <div
          className="limited-token-notice"
          onClick={() => window.location.href = '/upgrade?tier=unlimited&feature=TOKEN_ECONOMY'}
        >
          <span className="premium-icon">💎</span>
          <span>Unlock the token economy!</span>
        </div>
      )}
      
      {streakDays > 0 && (
        <div className="streak-display">
          <span className="streak-icon">🔥</span>
          <span className="streak-days">{streakDays}</span>
          <span className="streak-label">day streak!</span>
        </div>
      )}
      
      {!showReferralInfo && !showApplyReferral ? (
        <div className="token-actions">
          <button className="referral-button" onClick={fetchReferralCode}>
            <span className="referral-icon">👥</span> Invite friends
          </button>
          
          <button className="apply-referral-button" onClick={() => setShowApplyReferral(true)}>
            <span className="apply-referral-icon">🎁</span> Apply code
          </button>
          
          {!hasPremiumTokenFeatures && (
            <button
              className="upgrade-button"
              onClick={() => window.location.href = '/upgrade?tier=unlimited&feature=TOKEN_ECONOMY'}
            >
              <span className="upgrade-icon">⭐</span> Upgrade to Unlimited
            </button>
          )}
        </div>
      ) : showReferralInfo ? (
        <div className="referral-info">
          <p>Share your invite code:</p>
          <div className="referral-code">
            <code>{referralCode}</code>
            <button className="copy-button" onClick={copyReferralLink}>
              {referralCopied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          <p className="referral-reward">Your friends get 20 bonus tokens and you'll earn 50!</p>
          {!hasPremiumTokenFeatures && (
            <p className="premium-token-teaser">
              <span className="premium-icon">💎</span> 
              Premium users unlock the token economy with rewards!
            </p>
          )}
          <button className="back-button" onClick={() => setShowReferralInfo(false)}>
            ← Back to actions
          </button>
        </div>
      ) : showApplyReferral ? (
        <div className="apply-referral-info">
          <p>Enter a friend's referral code:</p>
          <div className="apply-referral-form">
            <input
              type="text"
              value={applyReferralCode}
              onChange={(e) => setApplyReferralCode(e.target.value)}
              placeholder="Enter referral code"
              className="referral-input"
              maxLength="10"
            />
            <button className="apply-button" onClick={handleApplyReferral}>
              Apply Code
            </button>
          </div>
          {applyReferralMessage && (
            <p className={`apply-referral-message ${applyReferralMessage.includes('success') ? 'success' : 'error'}`}>
              {applyReferralMessage}
            </p>
          )}
          <p className="apply-referral-reward">You and your friend both earn tokens when you apply their code!</p>
          <button className="back-button" onClick={() => {
            setShowApplyReferral(false);
            setApplyReferralCode('');
            setApplyReferralMessage('');
          }}>
            ← Back to actions
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TokenDisplay;